import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MicIcon from "@mui/icons-material/Mic";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

// ─── Feature 1: Playbook Scorecard ───────────────────────────────────────────

const playbookCriteria = [
  { label: "Needs Discovery", score: 95, status: "pass" },
  { label: "Value Proposition", score: 88, status: "pass" },
  { label: "Objection Handling", score: 72, status: "warn" },
  { label: "Closing Attempt", score: 91, status: "pass" },
  { label: "Follow-up Scheduled", score: 45, status: "fail" },
];

function PlaybookPreview() {
  return (
    <Box sx={{ p: 3, width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }}>
          Call Scorecard
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>78</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>/100</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {playbookCriteria.map((c) => (
          <Box key={c.label}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {c.status === "pass" && <CheckCircleIcon sx={{ fontSize: 15, color: "success.main" }} />}
                {c.status === "warn" && <WarningAmberIcon sx={{ fontSize: 15, color: "warning.main" }} />}
                {c.status === "fail" && <CancelIcon sx={{ fontSize: 15, color: "error.main" }} />}
                <Typography variant="caption" sx={{ color: "text.primary", fontWeight: 500 }}>{c.label}</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>{c.score}</Typography>
            </Box>
            <Box sx={{ height: 5, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${c.score}%`,
                  borderRadius: 3,
                  bgcolor: c.status === "pass" ? "success.main" : c.status === "warn" ? "warning.main" : "error.main",
                  transition: "width 0.6s ease",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>  
    </Box>
  );
}

// ─── Feature 2: Call Review ───────────────────────────────────────────────────

const summaryPoints = [
  { icon: "✅", text: "Strong rapport building in the opening 3 minutes" },
  { icon: "✅", text: "Client showed high interest in the pricing structure" },
  { icon: "⚠️", text: "Objection raised around implementation timeline" },
  { icon: "❌", text: "Missed opportunity to close — timeline concern unresolved" },
];

function CallReviewPreview() {
  return (
    <Box sx={{ p: 3, width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }}>
            AI Analysis
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>Discovery Call · 24 min</Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1 }}>8.4</Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>/ 10</Typography>
        </Box>
      </Box>

      {/* Waveform mock */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "2px", height: 28 }}>
        {[3,5,8,6,9,7,4,6,8,5,9,7,3,6,8,9,5,4,7,6,8,9,4,5,7,6,3,8,5,9,7,4,6,8,5,3].map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${h * 11}%`,
              minHeight: 3,
              borderRadius: 1,
              bgcolor: i < 14 ? "primary.main" : "action.disabled",
              opacity: i < 14 ? 0.85 : 0.35,
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
          AI Summary
        </Typography>
        {summaryPoints.map((p) => (
          <Box key={p.text} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.6 }}>{p.icon}</Typography>
            <Typography variant="caption" sx={{ color: "text.primary", lineHeight: 1.6 }}>{p.text}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: "auto" }}>
        {["discovery", "pricing", "timeline", "ROI", "objection"].map((kw) => (
          <Chip key={kw} label={kw} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.62rem", borderColor: "primary.light", color: "primary.main" }} />
        ))}
      </Box>
    </Box>
  );
}

// ─── Feature 3: Team Performance ─────────────────────────────────────────────

const teamReps = [
  { name: "Sarah K.", score: 94, trend: "up", delta: "+3" },
  { name: "Mike R.",  score: 87, trend: "up", delta: "+1" },
  { name: "Chris L.", score: 79, trend: "flat", delta: "0" },
  { name: "Dana M.",  score: 71, trend: "down", delta: "-2" },
  { name: "Tom B.",   score: 63, trend: "down", delta: "-4" },
];

function TeamPreview() {
  return (
    <Box sx={{ p: 3, width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }}>
          Team Leaderboard
        </Typography>
        <Chip label="This Month" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {teamReps.map((rep, i) => (
          <Box key={rep.name} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.disabled", width: 14, textAlign: "right", fontWeight: 600 }}>
              {i + 1}
            </Typography>
            <Box
              sx={{
                width: 26, height: 26, borderRadius: "50%",
                bgcolor: i === 0 ? "primary.main" : "action.selected",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: i === 0 ? "white" : "text.secondary" }}>
                {rep.name.split(" ").map((n) => n[0]).join("")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>{rep.name}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {rep.trend === "up" && <TrendingUpIcon sx={{ fontSize: 12, color: "success.main" }} />}
                  {rep.trend === "down" && <TrendingDownIcon sx={{ fontSize: 12, color: "error.main" }} />}
                  {rep.trend === "flat" && <TrendingFlatIcon sx={{ fontSize: 12, color: "text.disabled" }} />}
                  <Typography variant="caption" sx={{ color: rep.trend === "up" ? "success.main" : rep.trend === "down" ? "error.main" : "text.disabled", fontSize: "0.65rem" }}>
                    {rep.delta}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", ml: 0.5 }}>{rep.score}</Typography>
                </Box>
              </Box>
              <Box sx={{ height: 5, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${rep.score}%`,
                    borderRadius: 3,
                    bgcolor: i === 0 ? "primary.main" : i === 1 ? "primary.light" : "action.selected",
                    transition: "width 0.6s ease",
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: "auto", pt: 1.5, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "space-between" }}>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>Team avg <strong>78.8</strong></Typography>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>Top-performer gap <strong style={{ color: "#1976d2" }}>+15.2</strong></Typography>
      </Box>
    </Box>
  );
}

// ─── Items ────────────────────────────────────────────────────────────────────

const items = [
  {
    icon: <AnalyticsIcon />,
    title: "Your Playbook, Automated",
    description:
      "Define what a winning call looks like. AI scores every rep against your own standards, not a generic template. Train the system on your top closers.",
    preview: <PlaybookPreview />,
  },
  {
    icon: <MicIcon />,
    title: "Review 10 Calls in the Time It Takes to Listen to One",
    description:
      "Cut Call Review Time by 90%. Get instant summaries, scores and critical concerns for every call the second it's uploaded.",
    preview: <CallReviewPreview />,
  },
  {
    icon: <PeopleIcon />,
    title: "Team Performance",
    description:
      "Track individual and team metrics over time. Compare performance across team members and identify top performers and improvement areas.",
    preview: <TeamPreview />,
  },
];

// ─── Mobile Layout ────────────────────────────────────────────────────────────

function MobileLayout({ selectedItemIndex, handleItemClick, selectedFeature }: any) {
  if (!items[selectedItemIndex]) return null;

  return (
    <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 2 }}>
      {/* Stacked tab buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {items.map(({ icon, title }, index) => (
          <Box
            key={index}
            component={Button}
            onClick={() => handleItemClick(index)}
            sx={{
              p: 1.5,
              width: "100%",
              textAlign: "left",
              borderRadius: 2,
              border: "1px solid",
              borderColor: selectedItemIndex === index ? "primary.main" : "divider",
              background: selectedItemIndex === index
                ? "linear-gradient(to right, hsl(210, 98%, 48%), hsl(210, 98%, 35%))"
                : "transparent",
              "&:hover": { backgroundColor: selectedItemIndex === index ? undefined : "action.hover" },
              justifyContent: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, textTransform: "none" }}>
              <Box sx={{ color: selectedItemIndex === index ? "white" : "text.secondary", fontSize: 20, display: "flex" }}>
                {icon}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: selectedItemIndex === index ? "white" : "text.primary" }}
              >
                {title}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Preview card */}
      <Card variant="outlined">
        <Box sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {selectedFeature.preview}
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {selectedFeature.description}
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const handleItemClick = (index: number) => setSelectedItemIndex(index);

  const selectedFeature = items[selectedItemIndex];

  return (
    <Container id="features" sx={{ py: { xs: 8, sm: 16 } }}>
      <Box sx={{ width: { sm: "100%", md: "60%" } }}>
        <Typography component="h2" variant="h4" gutterBottom sx={{ color: "text.primary" }}>
          Stop Reviewing Calls Manually
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: { xs: 2, sm: 4 } }}>
          Upload a recording. In minutes you have scores, a summary, red flags, and coaching
          pointer - measured against criteria you define, not a generic template.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row-reverse" }, gap: 2 }}>
        <div>
          <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", gap: 2, height: "100%" }}>
            {items.map(({ icon, title, description }, index) => (
              <Box
                key={index}
                component={Button}
                onClick={() => handleItemClick(index)}
                sx={{
                  p: 2,
                  height: "100%",
                  width: "100%",
                  textAlign: "left",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: selectedItemIndex === index ? "primary.main" : "divider",
                  background: selectedItemIndex === index ? "action.selected" : "transparent",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    gap: 1,
                    textAlign: "left",
                    textTransform: "none",
                    color: selectedItemIndex === index ? "text.primary" : "text.secondary",
                  }}
                >
                  <Box sx={{ fontSize: 32 }}>{icon}</Box>
                  <Typography variant="h6">{title}</Typography>
                  <Typography variant="body2">{description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <MobileLayout
            selectedItemIndex={selectedItemIndex}
            handleItemClick={handleItemClick}
            selectedFeature={selectedFeature}
          />
        </div>

        {/* Preview panel */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, width: { xs: "100%", md: "70%" }, minHeight: 500 }}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              width: "100%",
              display: { xs: "none", sm: "flex" },
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            {selectedFeature.preview}
          </Card>
        </Box>
      </Box>
    </Container>
  );
}
